import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { tStatus } from "@/i18n";

// Small hand-rolled primitives in the dashboard palette (CSS variables in
// index.css; olive = surfaces, gold = accent, sand = text). Structured so
// shadcn/ui components can replace them one-for-one later.

/** A tiny inline spinner for a busy button (inherits the text colour). */
export function Spin({ className = "" }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={
        "inline-block h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 " +
        "border-current border-t-transparent " +
        className
      }
    />
  );
}

/** The primary button. `loading` is the "request in flight" state of a submit
 * button: a spinner in front of the label, the button disabled (so it can't be
 * pressed twice) and `aria-busy` for screen readers. */
export function Button({
  className = "",
  loading = false,
  disabled,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { loading?: boolean }) {
  return (
    <button
      className={
        "inline-flex items-center justify-center gap-2 rounded-md bg-gold px-4 py-2 " +
        "text-sm font-semibold text-olive-900 transition hover:bg-gold-300 " +
        "disabled:cursor-not-allowed disabled:opacity-50 " +
        className
      }
      {...props}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {loading && <Spin />}
      {children}
    </button>
  );
}

export function Input({
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={
        "w-full rounded-md border border-olive-600 bg-olive-900 px-3 py-2 text-sm " +
        "text-sand placeholder:text-sand-muted focus:border-gold focus:outline-none " +
        className
      }
      {...props}
    />
  );
}

export function Card({ children }: { children: ReactNode }) {
  return (
    <div className="nm-card p-5">
      {children}
    </div>
  );
}

export function Spinner() {
  const { t } = useTranslation();
  return (
    <div className="flex justify-center py-10 text-sm text-sand-muted">
      {t("common.loading")}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-700">
      {message}
    </div>
  );
}

// A status reads by its colour at a glance: green = done, amber = still ahead,
// blue = the driver / client confirmed, teal = under way, orange = needs
// attention, red = failed, grey = dead. Filled pills, no outline.
const GREEN = "bg-[#DCF5E6] text-[#166534]";
const AMBER = "bg-[#FEF3C7] text-[#92400E]";
const BLUE = "bg-[#E0EEFB] text-[#1D4E89]";
const TEAL = "bg-[#E0F2EF] text-[#1E6B65]";
const ORANGE = "bg-[#FFEDD5] text-[#9A3412]";
const RED = "bg-[#FEECEC] text-[#B42318]";
const GREY = "bg-[#EEF1F0] text-[#4F5E5B]";
const BADGE_TONE: Record<string, string> = {
  done: GREEN,
  completed: GREEN,
  paid: GREEN,
  settled: GREEN,
  published: GREEN,
  synced: GREEN,
  sent: GREEN,
  active: GREEN,
  approved: GREEN,
  upcoming: AMBER,
  pending: AMBER,
  pending_approval: AMBER,
  awaiting_approval: AMBER,
  awaiting_information: AMBER,
  queued: AMBER,
  quoted: AMBER,
  confirmed: BLUE,
  driver_confirmed: BLUE,
  partial: TEAL,
  assigned: TEAL,
  driver_assigned: TEAL,
  in_progress: TEAL,
  running: TEAL,
  publishing: TEAL,
  attention: ORANGE,
  unassigned: ORANGE,
  needs_data: ORANGE,
  handed_back: ORANGE,
  failed: RED,
  rejected: RED,
  refused: RED,
  cancelled: GREY,
  refunded: GREY,
  draft: GREY,
  archived: GREY,
  inactive: GREY,
  skipped: GREY,
};
const BADGE_DEFAULT = GREY;

export function StatusBadge({ status }: { status: string }) {
  // Subscribe to language changes so tStatus re-resolves.
  useTranslation();
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        BADGE_TONE[status] ?? BADGE_DEFAULT
      }`}
    >
      {tStatus(status)}
    </span>
  );
}

/** A booking's status as the owner reads it: derived from its lines (done /
 * partial / upcoming / attention) when it has any, the stored status otherwise. */
export function BookingStatusBadge({
  booking,
}: {
  booking: { status: string; display_status?: string | null };
}) {
  return <StatusBadge status={booking.display_status ?? booking.status} />;
}

/** A centred dialog over a dimmed backdrop. It closes only from its own
 * buttons — not on Escape or a backdrop click — because the forms in it are
 * long and the date pickers inside already use Escape for themselves. */
export function Modal({
  title,
  onClose,
  busy = false,
  size = "lg",
  children,
}: {
  title: string;
  onClose: () => void;
  /** `sm` — a short question / confirmation; `lg` — a form. */
  size?: "sm" | "lg";
  /** A save is in flight: the × is disabled so the window can't be dismissed
   * (and its outcome missed) halfway. */
  busy?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-ink/40 p-3 sm:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={
          "my-4 w-full rounded-card border border-[rgb(var(--nm-card-border))] bg-olive-800 p-5 shadow-xl " +
          (size === "sm" ? "max-w-md" : "max-w-4xl")
        }
      >
        <div className="mb-4 flex items-center gap-3">
          <h2 className="text-base font-semibold text-sand">{title}</h2>
          <button
            type="button"
            aria-label={t("common.close")}
            className="ml-auto text-lg leading-none text-sand-muted hover:text-sand disabled:opacity-40"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
